import React from 'react';
import {
  Box, Typography, TextField, Card, CardContent, Grid,
  Button, ListItem, ListItemText, IconButton, Tooltip
} from '@mui/material';
import { Assignment, CloudUpload, Delete } from '@mui/icons-material';

const LicensingCertificationStep = ({
  formik,
  handleFileChange,
  addItem,
  removeItem,
  filePreviews,
  textFieldStyles
}) => {
  return (
    <Box
      sx={{
        backgroundColor: '#f9f9f9',
        padding: '30px',
        borderRadius: '12px',
        boxShadow: '0px 4px 10px rgba(0, 0, 0, 0.1)',
        maxWidth: '800px',
        margin: 'auto'
      }}
    >
      <Typography
        variant='h5'
        gutterBottom
        sx={{ color: '#1976d2', textAlign: 'center', mb: 3, fontWeight: 'bold' }}
      >
        <Assignment sx={{ verticalAlign: 'middle', mr: 1 }} />
        Upload License & Certifications
      </Typography>

      {/* License Number Input */}
      <TextField
        name='license_number'
        label='License Number'
        fullWidth
        required
        error={!!formik.errors.license_number}
        helperText={formik.errors.license_number}
        onChange={formik.handleChange}
        value={formik.values.license_number}
        sx={{ ...textFieldStyles, mb: 3 }}
      />

      {/* License Upload Section */}
      <Card variant='outlined' sx={{ mb: 3, backgroundColor: '#e4e4e4', color: '#425b6f' }}>
        <CardContent>
          <Typography variant='h6' sx={{ mb: 2 }}>License Documents</Typography>

          {/* Add New License */}
          <Grid container spacing={2} alignItems='center'>
            <Grid item xs={5}>
              <TextField
                label='License Name'
                name='newLicense.name'
                value={formik.values.newLicense?.name || ''}
                onChange={formik.handleChange}
                fullWidth
                required
                sx={textFieldStyles}
              />
            </Grid>
            <Grid item xs={5}>
              <TextField
                label='Issued By'
                name='newLicense.issued_by'
                value={formik.values.newLicense?.issued_by || ''}
                onChange={formik.handleChange}
                fullWidth
                required
                sx={textFieldStyles}
              />
            </Grid>
            <Grid item xs={5}>
              <TextField
                label='Issue Date'
                type='date'
                name='newLicense.issue_date'
                value={formik.values.newLicense?.issue_date || ''}
                onChange={formik.handleChange}
                fullWidth
                required
                InputLabelProps={{ shrink: true }}
                sx={textFieldStyles}
              />
            </Grid>
            <Grid item xs={5}>
              <TextField
                label='Expiry Date'
                type='date'
                name='newLicense.expiry_date'
                value={formik.values.newLicense?.expiry_date || ''}
                onChange={formik.handleChange}
                fullWidth
                InputLabelProps={{ shrink: true }}
                sx={textFieldStyles}
              />
            </Grid>
            <Grid item xs={2} sx={{ textAlign: 'center' }}>
              <Button
                component='label'
                variant='contained'
                startIcon={<CloudUpload />}
                sx={{ backgroundColor: '#1976d2', color: '#fff', '&:hover': { backgroundColor: '#155a9f' } }}
              >
                Upload
                <input
                  type='file'
                  name='licenses'
                  accept='.jpg,.pdf,.png'
                  hidden
                  onChange={(e) => {
                    handleFileChange(e);
                    addItem('licenses', { ...formik.values.newLicense, file: e.target.files[0] });
                    formik.setFieldValue('newLicense', { name: '', issued_by: '', issue_date: '', expiry_date: '' });
                  }}
                />
              </Button>
            </Grid>
          </Grid>

          {/* Display Uploaded License Files */}
          {formik.values.licenses?.map((license, index) => {
            const file = license.file;
            const filePreviewUrl = filePreviews[file.name];

            return (
              <ListItem
                key={index} secondaryAction={
                  <Tooltip title='Remove'>
                    <IconButton color='error' onClick={() => removeItem('licenses', index)}>
                      <Delete />
                    </IconButton>
                  </Tooltip>
              }
              >
                {/* Display Image Preview */}
                {filePreviewUrl && file.type.startsWith('image/') && (
                  <Box
                    component='img'
                    src={filePreviewUrl}
                    alt='license-preview'
                    sx={{ width: 50, height: 50, borderRadius: 1, mr: 2 }}
                  />
                )}

                {/* Display PDF Link */}
                {filePreviewUrl && file.type === 'application/pdf' && (
                  <Tooltip title='View PDF'>
                    <a href={filePreviewUrl} target='_blank' rel='noopener noreferrer'>
                      <IconButton>
                        <CloudUpload sx={{ color: '#1976d2' }} />
                      </IconButton>
                    </a>
                  </Tooltip>
                )}

                {/* Clickable File Name */}
                <ListItemText
                  primary={
                    <a href={filePreviewUrl} target='_blank' rel='noopener noreferrer' style={{ textDecoration: 'none', color: '#1976d2' }}>
                      {license.name} (Issued By: {license.issued_by})
                    </a>
                  }
                  secondary={`Issue Date: ${license.issue_date} | Expiry Date: ${license.expiry_date}`}
                  secondaryTypographyProps={{ sx: { color: '#8999a5' } }}
                />
              </ListItem>
            );
          })}
        </CardContent>
      </Card>

      {/* Certification Upload Section */}
      <Card variant='outlined' sx={{ mt: 2, backgroundColor: '#e4e4e4', color: '#425b6f' }}>
        <CardContent>
          <Typography variant='h6' sx={{ mb: 2 }}>Certifications</Typography>

          {/* Add New Certification */}
          <Grid container spacing={2} alignItems='center'>
            <Grid item xs={5}>
              <TextField
                label='Certification Name'
                name='newCertification.name'
                value={formik.values.newCertification?.name || ''}
                onChange={formik.handleChange}
                fullWidth
                required
                sx={textFieldStyles}
              />
            </Grid>
            <Grid item xs={5}>
              <TextField
                label='Issued By'
                name='newCertification.issued_by'
                value={formik.values.newCertification?.issued_by || ''}
                onChange={formik.handleChange}
                fullWidth
                required
                sx={textFieldStyles}
              />
            </Grid>
            <Grid item xs={5}>
              <TextField
                label='Issue Date'
                type='date'
                name='newCertification.issue_date'
                value={formik.values.newCertification?.issue_date || ''}
                onChange={formik.handleChange}
                fullWidth
                required
                InputLabelProps={{ shrink: true }}
                sx={textFieldStyles}
              />
            </Grid>
            <Grid item xs={5}>
              <TextField
                label='Expiry Date'
                type='date'
                name='newCertification.expiry_date'
                value={formik.values.newCertification?.expiry_date || ''}
                onChange={formik.handleChange}
                fullWidth
                InputLabelProps={{ shrink: true }}
                sx={textFieldStyles}
              />
            </Grid>
            <Grid item xs={2} sx={{ textAlign: 'center' }}>
              <Button
                component='label'
                variant='contained'
                startIcon={<CloudUpload />}
                sx={{ backgroundColor: '#1976d2', color: '#fff', '&:hover': { backgroundColor: '#155a9f' } }}
              >
                Upload
                <input
                  type='file'
                  name='certifications'
                  accept='.jpg,.pdf,.png'
                  hidden
                  onChange={(e) => {
                    handleFileChange(e);
                    addItem('certifications', { ...formik.values.newCertification, file: e.target.files[0] });
                    formik.setFieldValue('newCertification', { name: '', issued_by: '', issue_date: '', expiry_date: '' });
                  }}
                />
              </Button>
            </Grid>
          </Grid>

          {/* Display Uploaded Certification Files */}
          {formik.values.certifications?.map((certification, index) => {
            const file = certification.file;
            const filePreviewUrl = filePreviews[file.name];

            return (
              <ListItem
                key={index} secondaryAction={
                  <Tooltip title='Remove'>
                    <IconButton color='error' onClick={() => removeItem('certifications', index)}>
                      <Delete />
                    </IconButton>
                  </Tooltip>
              }
              >
                {/* Display Image Preview */}
                {filePreviewUrl && file.type.startsWith('image/') && (
                  <Box
                    component='img'
                    src={filePreviewUrl}
                    alt='certification-preview'
                    sx={{ width: 50, height: 50, borderRadius: 1, mr: 2 }}
                  />
                )}

                {/* Display PDF Link */}
                {filePreviewUrl && file.type === 'application/pdf' && (
                  <Tooltip title='View PDF'>
                    <a href={filePreviewUrl} target='_blank' rel='noopener noreferrer'>
                      <IconButton>
                        <CloudUpload sx={{ color: '#1976d2' }} />
                      </IconButton>
                    </a>
                  </Tooltip>
                )}

                {/* Clickable File Name */}
                <ListItemText
                  primary={
                    <a href={filePreviewUrl} target='_blank' rel='noopener noreferrer' style={{ textDecoration: 'none', color: '#1976d2' }}>
                      {certification.name} (Issued By: {certification.issued_by})
                    </a>
                  }
                  secondary={`Issue Date: ${certification.issue_date} | Expiry Date: ${certification.expiry_date}`}
                  secondaryTypographyProps={{ sx: { color: '#8999a5' } }}
                />
              </ListItem>
            );
          })}
        </CardContent>
      </Card>
    </Box>
  );
};

export default LicensingCertificationStep;
